/**
 * Comprehensive Notification Service
 * Handles all types of notifications: lab results, appointments, vet visits, etc.
 * Enhanced with value-delivering health alert notifications
 */

import { collection, addDoc, query, where, getDocs, orderBy, limit, doc, updateDoc, onSnapshot, Timestamp, collectionGroup, DocumentData } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logger } from './logger';
import { handleError } from '../utils/errorUtils';
import {
  NotificationCategory,
  HealthConcern,
  HealthAlertDetails,
  NotificationPriority
} from '../types/notifications';
import NotificationContentBuilder from './notificationContentBuilder';

export interface Notification {
  id: string;
  userId: string;
  petId?: string;
  petName?: string;
  
  // Support both new category and legacy type (for backward compatibility)
  category?: NotificationCategory;
  type?: 'lab_result' | 'appointment' | 'vet_visit' | 'medication' | 'vaccination' | 'review_request' | 'medication_review' | 'system' | 'reminder' | 'expense' | 'quarantine_alert' | 'message';
  
  priority: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  message: string;
  data?: Record<string, unknown>; // Additional data specific to notification type
  
  // Enhanced health alert details
  healthAlertDetails?: HealthAlertDetails;
  
  read: boolean;
  createdAt: Date;
  expiresAt?: Date;
  actionUrl?: string; // URL to navigate to when clicked
  actionLabel?: string; // Button text for action
  
  // Additional metadata
  sourceDocumentIds?: string[];
  sourceEventType?: string;
  channels?: string[];
  batchId?: string;
  batchCount?: number;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

type NotificationListener = (notifications: Notification[]) => void;
type StatsListener = (stats: NotificationStats) => void;

class NotificationService {
  private static listeners: Set<NotificationListener> = new Set();
  private static statsListeners: Set<StatsListener> = new Set();
  private static unsubscribeSnapshot?: () => void;
  private static currentUserId?: string;
  private static log = logger.createScope('NotificationService');

  /**
   * Determine if a notification is an action item that requires user attention
   * Action items include:
   * - Lab results with abnormal values or health concerns
   * - Appointment reminders (upcoming appointments)
   * - Medication reminders
   * - Vaccination reminders
   * - Follow-up appointment needs
   * 
   * Non-action items (filtered out):
   * - Lab results processed (normal)
   * - Vet visit summaries processed
   * - New appointment created
   * - New message arrived
   */
  static isActionItemNotification(notification: Notification): boolean {
    const category = notification.category;
    const type = notification.type;
    const title = notification.title?.toLowerCase() || '';
    const message = notification.message?.toLowerCase() || '';
    const data = notification.data || {};
    
    // Health alerts (abnormal lab results) are always action items
    if (category === 'health_alert') {
      return true;
    }
    
    // Appointment reminders are action items
    if (category === 'appointment_reminder' || 
        (type === 'appointment' && (title.includes('upcoming') || title.includes('reminder') || title.includes('tomorrow')))) {
      return true;
    }
    
    // Care tasks (medications, vaccinations) are action items
    if (category === 'care_task' || type === 'medication' || type === 'vaccination') {
      return true;
    }
    
    // Lab results - show all (both abnormal and normal processed)
    if (type === 'lab_result') {
      // Check if it has abnormal values or needs review
      if (data.hasAbnormalValues === true || 
          data.urgency === 'urgent' || 
          data.urgency === 'critical' || 
          data.urgency === 'attention_needed' ||
          title.includes('need review') || 
          title.includes('abnormal') ||
          title.includes('attention')) {
        return true;
      }
      // Normal lab results processed - show in notification bell as informational
      // User wants to see confirmation that documents were processed
      if (data.isInformational === true || title.includes('processed')) {
        return true;
      }
      return false;
    }
    
    // Quarantine alerts are action items (require review)
    if (category === 'quarantine_alert' || (type && type === 'quarantine_alert')) {
      return true;
    }
    
    // Review requests are action items (including medication reviews)
    if (type === 'review_request' || type === 'medication_review') {
      return true;
    }
    
    // Action required category notifications are action items
    if (category === 'action_required') {
      return true;
    }
    
    // Vet visit summaries - show all (both with follow-up and processed)
    if (type === 'vet_visit' || category === 'records_processed') {
      // Show if there's a follow-up recommendation or action needed
      if (message.includes('follow-up') || message.includes('recommendation') || message.includes('action')) {
        return true;
      }
      // Also show processed vet visits as informational - user wants confirmation
      if (data.isInformational === true || title.includes('processed')) {
        return true;
      }
      return false;
    }
    
    // - New appointment created (not reminders)
    if (category === 'appointment_added' || 
        (type === 'appointment' && title.includes('scheduled') && !title.includes('upcoming') && !title.includes('reminder'))) {
      return false;
    }
    
    // - New messages (if we have message notifications)
    if ((type && type === 'message') || message.includes('new message')) {
      return false;
    }
    
    // Note: Normal lab results processed are now shown (handled above in lab_result type check)
    
    // - Expense notifications (not action items unless there's a payment due)
    if (type === 'expense') {
      return false;
    }
    
    // System notifications are generally not action items unless urgent
    if (type === 'system' && notification.priority !== 'urgent' && notification.priority !== 'high') {
      return false;
    }
    
    // Default: if it's urgent or high priority, treat as action item
    if (notification.priority === 'urgent' || notification.priority === 'high') {
      return true;
    }
    
    // Otherwise, not an action item
    return false;
  }

  /**
   * Subscribe to real-time notifications for a user
   */
  static subscribeToNotifications(userId: string, callback: NotificationListener): () => void {
    const listener = (notifications: Notification[]) => {
      callback(notifications);
    };
    
    this.listeners.add(listener);
    
    // Set up real-time listener if not already active or if user changed
    if (!this.unsubscribeSnapshot || this.currentUserId !== userId) {
      if (this.unsubscribeSnapshot) {
        this.unsubscribeSnapshot();
      }
      this.currentUserId = userId;
      this.setupRealtimeListener(userId);
    }
    
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.unsubscribeSnapshot?.();
        this.unsubscribeSnapshot = undefined;
        this.currentUserId = undefined;
      }
    };
  }

  /**
   * Subscribe to notification statistics
   */
  static subscribeToStats(userId: string, callback: StatsListener): () => void {
    const listener = (stats: NotificationStats) => {
      callback(stats);
    };
    
    this.statsListeners.add(listener);
    
    // Set up real-time listener if not already active or if user changed
    if (!this.unsubscribeSnapshot || this.currentUserId !== userId) {
      if (this.unsubscribeSnapshot) {
        this.unsubscribeSnapshot();
      }
      this.currentUserId = userId;
      this.setupRealtimeListener(userId);
    }
    
    return () => {
      this.statsListeners.delete(listener);
      if (this.statsListeners.size === 0) {
        this.unsubscribeSnapshot?.();
        this.unsubscribeSnapshot = undefined;
        this.currentUserId = undefined;
      }
    };
  }

  /**
   * Set up real-time Firestore listener
   */
  private static setupRealtimeListener(userId: string) {
    const userScopedRef = collection(db, `users/${userId}/notifications`);
    const qUser = query(userScopedRef, where('deleted', '==', false), orderBy('createdAt', 'desc'), limit(50));

    const legacyRef = collection(db, 'notifications');
    const qLegacy = query(legacyRef, where('userId', '==', userId), where('deleted', '==', false), orderBy('createdAt', 'desc'), limit(50));

    let userRows: Notification[] = [];
    let legacyRows: Notification[] = [];

    const emit = () => {
      const combined = [...userRows, ...legacyRows]
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, 50);

      // Filter to only action item notifications
      const actionItemNotifications = combined.filter(n => this.isActionItemNotification(n));

      const stats: NotificationStats = { total: 0, unread: 0, byType: {}, byPriority: {} };
      actionItemNotifications.forEach(n => {
        stats.total++;
        if (!n.read) stats.unread++;
        
        // Use type if available, otherwise use category
        const typeKey = n.type || n.category || 'other';
        stats.byType[typeKey] = (stats.byType[typeKey] || 0) + 1;
        stats.byPriority[n.priority] = (stats.byPriority[n.priority] || 0) + 1;
      });

      NotificationService.log.debug('Emitting filtered notifications', { 
        totalBeforeFilter: combined.length, 
        totalAfterFilter: actionItemNotifications.length,
        unread: stats.unread 
      });

      // Only send action item notifications to listeners
      this.listeners.forEach(l => l(actionItemNotifications));
      this.statsListeners.forEach(l => l(stats));
    };

    const unsubUser = onSnapshot(qUser, (snapshot) => {
      userRows = snapshot.docs.map(d => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          userId: userId,
          petId: data.petId,
          petName: data.petName,
          category: data.category,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          data: data.data,
          healthAlertDetails: data.healthAlertDetails,
          read: data.read,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          sourceDocumentIds: data.sourceDocumentIds,
          sourceEventType: data.sourceEventType,
          channels: data.channels,
          batchId: data.batchId,
          batchCount: data.batchCount
        } as Notification;
      });
      emit();
    }, (error) => {
      NotificationService.log.error('Notifications user-scoped listener error', error as Error, { userId });
    });

    const unsubLegacy = onSnapshot(qLegacy, (snapshot) => {
      legacyRows = snapshot.docs.map(d => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          userId: data.userId,
          petId: data.petId,
          petName: data.petName,
          category: data.category,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          data: data.data,
          healthAlertDetails: data.healthAlertDetails,
          read: data.read,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          sourceDocumentIds: data.sourceDocumentIds,
          sourceEventType: data.sourceEventType,
          channels: data.channels,
          batchId: data.batchId,
          batchCount: data.batchCount
        } as Notification;
      });
      emit();
    }, (error) => {
      NotificationService.log.error('Notifications legacy listener error', error as Error, { userId });
    });

    this.unsubscribeSnapshot = () => {
      try { unsubUser(); } catch {}
      try { unsubLegacy(); } catch {}
    };
    
    // Force initial emit with empty arrays to ensure listeners get the correct initial state
    emit();
  }

  /**
   * Force refresh notifications and stats for the current user
   * This can be called to ensure the latest filtered data is emitted
   */
  static async refreshNotifications(userId: string): Promise<void> {
    try {
      // If listener is already set up, it will automatically emit on next data change
      // But we can also manually trigger by re-setting up the listener
      if (this.unsubscribeSnapshot && this.currentUserId === userId) {
        // Listener is already active, it will update automatically
        NotificationService.log.debug('Notifications listener already active, will update automatically', { userId });
      } else {
        // Set up listener if not already active
        if (this.unsubscribeSnapshot) {
          this.unsubscribeSnapshot();
        }
        this.currentUserId = userId;
        this.setupRealtimeListener(userId);
      }
    } catch (error) {
      NotificationService.log.error('Error refreshing notifications', error as Error, { userId });
    }
  }

  /**
   * Create a new notification
   * Checks user preferences before creating notification
   */
  static async createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Promise<string> {
    try {
      // Check user preferences before creating notification
      try {
        const NotificationPreferencesService = (await import('./notificationPreferencesService')).default;
        const shouldSend = await NotificationPreferencesService.shouldSendNotification(
          notification.userId,
          notification.category || 'system',
          notification.priority,
          'in_app' // Check in_app channel (push will be handled by Cloud Function)
        );
        
        if (!shouldSend) {
          NotificationService.log.debug('Notification blocked by user preferences', {
            userId: notification.userId,
            category: notification.category,
            priority: notification.priority
          });
          // Still create the notification in Firestore (for history)
          // But mark it as not sent
        }
      } catch (prefError) {
        // If preferences check fails, proceed with creating notification
        NotificationService.log.warn('Error checking notification preferences, proceeding anyway', {
          error: prefError as Error,
          userId: notification.userId
        });
      }
      
      // For backward compatibility: if category is provided but type is not, map category to type
      const notificationData: Record<string, unknown> = {
        ...notification,
        read: false,
        createdAt: Timestamp.now(),
        deleted: false
      };
      
      // Map category to legacy type for backward compatibility
      if (notification.category && !notification.type) {
        const categoryToTypeMap: Record<string, string> = {
          'health_alert': 'lab_result',
          'appointment_reminder': 'appointment',
          'appointment_added': 'appointment',
          'care_task': 'medication',
          'records_processed': 'vet_visit',
          'system': 'system'
        };
        notificationData.type = categoryToTypeMap[notification.category] || 'system';
      }
      
      const docRef = await addDoc(collection(db, `users/${notification.userId}/notifications`), notificationData);
      
      NotificationService.log.info('Notification created', { 
        notificationId: docRef.id, 
        category: notification.category,
        type: notificationData.type, 
        userId: notification.userId 
      });
      return docRef.id;
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'createNotification', userId: notification.userId } });
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string, userId?: string): Promise<void> {
    try {
      if (userId) {
        await updateDoc(doc(db, `users/${userId}/notifications/${notificationId}`), { read: true });
        return;
      }
      await updateDoc(doc(db, 'notifications', notificationId), { read: true });
      NotificationService.log.debug('Notification marked as read', { notificationId });
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'markAsRead' } });
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const userRef = collection(db, `users/${userId}/notifications`);
      const qUser = query(userRef, where('read', '==', false));
      const snapUser = await getDocs(qUser);
      const upUser = snapUser.docs.map(d => updateDoc(d.ref, { read: true }));

      const legacyRef = collection(db, 'notifications');
      const qLegacy = query(legacyRef, where('userId', '==', userId), where('read', '==', false));
      const snapLegacy = await getDocs(qLegacy);
      const upLegacy = snapLegacy.docs.map(d => updateDoc(d.ref, { read: true }));

      await Promise.all([...upUser, ...upLegacy]);
      NotificationService.log.info('All notifications marked as read', { userId });
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'markAllAsRead', userId } });
      throw error;
    }
  }

  /**
   * Delete notification
   */
  static async deleteNotification(notificationId: string, userId?: string): Promise<void> {
    try {
      if (userId) {
        await updateDoc(doc(db, `users/${userId}/notifications/${notificationId}`), { deleted: true, deletedAt: Timestamp.now() });
        return;
      }
      await updateDoc(doc(db, 'notifications', notificationId), { deleted: true, deletedAt: Timestamp.now() });
      NotificationService.log.info('Notification deleted', { notificationId });
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'deleteNotification' } });
      throw error;
    }
  }

  /**
   * Delete multiple notifications
   */
  static async deleteMultipleNotifications(notificationIds: string[], userId?: string): Promise<void> {
    try {
      if (userId) {
        const deletes = notificationIds.map(id => updateDoc(doc(db, `users/${userId}/notifications/${id}`), { deleted: true, deletedAt: Timestamp.now() }));
        await Promise.all(deletes);
      }
      const legacyDeletes = notificationIds.map(id => updateDoc(doc(db, 'notifications', id), { deleted: true, deletedAt: Timestamp.now() }));
      await Promise.all(legacyDeletes);
      NotificationService.log.info('Multiple notifications deleted', { count: notificationIds.length });
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'deleteMultipleNotifications' } });
      throw error;
    }
  }

  /**
   * Delete all test notifications for a user
   */
  static async deleteTestNotifications(userId: string): Promise<number> {
    try {
      const notificationsRef = collection(db, `users/${userId}/notifications`);
      const q = query(notificationsRef, where('title', '==', '🧪 Test Push Notification'));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { deleted: true, deletedAt: Timestamp.now() })
      );
      
      await Promise.all(deletePromises);
      const count = snapshot.docs.length;
      
      NotificationService.log.info('Test notifications deleted', { userId, count });
      return count;
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'deleteTestNotifications', userId } });
      throw error;
    }
  }

  /**
   * Delete all notifications for a user
   */
  static async deleteAllNotifications(userId: string): Promise<void> {
    try {
      const userRef = collection(db, `users/${userId}/notifications`);
      const qUser = query(userRef, where('deleted', '==', false));
      const snapUser = await getDocs(qUser);
      const delUser = snapUser.docs.map(d => updateDoc(d.ref, { deleted: true, deletedAt: Timestamp.now() }));

      const legacyRef = collection(db, 'notifications');
      const qLegacy = query(legacyRef, where('userId', '==', userId), where('deleted', '==', false));
      const snapLegacy = await getDocs(qLegacy);
      const delLegacy = snapLegacy.docs.map(d => updateDoc(d.ref, { deleted: true, deletedAt: Timestamp.now() }));

      await Promise.all([...delUser, ...delLegacy]);
      NotificationService.log.info('All notifications deleted for user', { userId });
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'deleteAllNotifications', userId } });
      throw error;
    }
  }

  /**
   * Get notifications for a user
   */
  static async getNotifications(userId: string, limitCount: number = 20): Promise<Notification[]> {
    try {
      const userRef = collection(db, `users/${userId}/notifications`);
      const qUser = query(userRef, where('deleted', '==', false), orderBy('createdAt', 'desc'), limit(limitCount));
      const snapUser = await getDocs(qUser);
      const userNotifs = snapUser.docs.map(d => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          userId,
          petId: data.petId,
          petName: data.petName,
          category: data.category,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          data: data.data,
          healthAlertDetails: data.healthAlertDetails,
          read: data.read,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          sourceDocumentIds: data.sourceDocumentIds,
          sourceEventType: data.sourceEventType,
          channels: data.channels,
          batchId: data.batchId,
          batchCount: data.batchCount
        } as Notification;
      });

      const legacyRef = collection(db, 'notifications');
      const qLegacy = query(legacyRef, where('userId', '==', userId), where('deleted', '==', false), orderBy('createdAt', 'desc'), limit(limitCount));
      const snapLegacy = await getDocs(qLegacy);
      const legacyNotifs = snapLegacy.docs.map(d => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          userId: data.userId,
          petId: data.petId,
          petName: data.petName,
          category: data.category,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          data: data.data,
          healthAlertDetails: data.healthAlertDetails,
          read: data.read,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          sourceDocumentIds: data.sourceDocumentIds,
          sourceEventType: data.sourceEventType,
          channels: data.channels,
          batchId: data.batchId,
          batchCount: data.batchCount
        } as Notification;
      });

      const allNotifications = [...userNotifs, ...legacyNotifs]
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      // Filter to only action item notifications
      const actionItemNotifications = allNotifications
        .filter(n => this.isActionItemNotification(n))
        .slice(0, limitCount);
      
      return actionItemNotifications;
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'getNotifications', userId } });
      throw error;
    }
  }

  /**
   * Create lab result notification
   * Only creates notification if there are abnormal values requiring action
   */
  static async notifyLabResultProcessed(
    userId: string,
    petId: string,
    petName: string,
    documentType: string,
    hasAbnormalValues: boolean,
    documentId: string,
    labName?: string
  ): Promise<string | null> {
    // If there are abnormal values, create a high-priority notification
    if (hasAbnormalValues) {
      const priority = 'high';
      const title = `⚠️ Lab Results Need Review - ${petName}`;
      const message = `Lab results for ${petName} have been processed. Some values may need your attention.`;

      return this.createNotification({
        userId,
        petId,
        type: 'lab_result',
        category: 'health_alert',
        priority,
        title,
        message,
        data: { documentId, documentType, hasAbnormalValues },
        actionUrl: `/health-insights?tab=timeline&filter=labs`,
        actionLabel: 'View Results'
      });
    }
    
    // For normal lab results, create an informational notification
    // User wants to see confirmation that documents were processed
    const providerText = labName ? ` from ${labName}` : '';
    return this.createNotification({
      userId,
      petId,
      type: 'lab_result',
      category: 'records_processed',
      priority: 'low',
      title: `✅ Lab Results Processed - ${petName}`,
      message: `Lab results for ${petName}${providerText} have been processed and added to your pet's health records.`,
      data: { 
        documentId,
        documentType,
        labName,
        isNormal: true,
        isInformational: true 
      },
      actionUrl: `/health-insights/lab-results/${petId}/${documentId}`,
      actionLabel: 'View Results'
    });
  }

  /**
   * Create appointment notification
   * NOTE: This is disabled - we don't notify when appointments are created, only for reminders
   */
  static async notifyAppointmentCreated(
    userId: string,
    petId: string,
    petName: string,
    appointmentData: { id?: string; [key: string]: unknown }
  ): Promise<string | null> {
    // Don't create notifications for new appointments - only for reminders
    NotificationService.log.debug('Skipping notification for new appointment creation (not an action item)', {
      userId,
      petId,
      appointmentId: appointmentData.id
    });
    return null;
  }

  /**
   * Create upcoming appointment reminder
   */
  static async notifyUpcomingAppointment(
    userId: string,
    petId: string,
    petName: string,
    appointmentData: { startTime?: string | Date },
    hoursUntil: number
  ): Promise<string> {
    const priority = hoursUntil <= 2 ? 'urgent' : hoursUntil <= 24 ? 'high' : 'medium';
    const title = `⏰ Upcoming Appointment - ${petName}`;
    const message = hoursUntil <= 2 
      ? `Appointment for ${petName} is in ${hoursUntil} hours!`
      : `Appointment for ${petName} is tomorrow at ${new Date(appointmentData.startTime || new Date()).toLocaleTimeString()}.`;

    return this.createNotification({
      userId,
      petId,
      type: 'appointment',
      priority,
      title,
      message,
      data: appointmentData,
      actionUrl: '/calendar',
      actionLabel: 'View Details'
    });
  }

  /**
   * Create vet visit summary notification
   * Creates action item notification if follow-up needed, otherwise shows informational toast
   */
  static async notifyVetVisitSummary(
    userId: string,
    petId: string,
    petName: string,
    visitData: { followUpNeeded?: boolean; recommendations?: string[]; actionRequired?: boolean; providerName?: string; vetName?: string; documentId?: string; [key: string]: unknown }
  ): Promise<string | null> {
    // Check if there's a follow-up action needed
    const hasFollowUp = visitData.followUpNeeded ||
                       visitData.recommendations?.length ||
                       visitData.actionRequired;
    
    if (hasFollowUp) {
      // Create persistent notification for action items
      const title = `🏥 Follow-up Needed - ${petName}`;
      const message = `Visit summary for ${petName} has follow-up recommendations that need your attention.`;

      return this.createNotification({
        userId,
        petId,
        type: 'vet_visit',
        priority: 'medium',
        title,
        message,
        data: { ...visitData, followUpNeeded: true },
        actionUrl: `/health-insights?tab=timeline&filter=vet-visits`,
        actionLabel: 'View Summary'
      });
    }
    
    // No follow-up needed - create persistent notification in notification bell
    // When clicked, it will navigate to the vet visit and mark as read
    const providerText = visitData.providerName || visitData.vetName ? ` from ${visitData.providerName || visitData.vetName}` : '';
    // Link directly to the specific vet visit if documentId is available, otherwise to timeline
    const actionUrl = visitData.documentId 
      ? `/health-insights?tab=timeline&documentId=${visitData.documentId}`
      : `/health-insights?tab=timeline&filter=vet-visits`;
    
    return this.createNotification({
      userId,
      petId,
      type: 'vet_visit',
      category: 'records_processed',
      priority: 'low',
      title: `✅ Vet Visit Processed - ${petName}`,
      message: `Vet visit summary for ${petName}${providerText} has been processed and added to your pet's health records.`,
      data: { 
        ...visitData,
        isInformational: true,
        followUpNeeded: false 
      },
      actionUrl,
      actionLabel: 'View Summary'
    });
  }

  /**
   * Create medication reminder notification
   */
  static async notifyMedicationReminder(
    userId: string,
    petId: string,
    petName: string,
    medicationData: { medicationName?: string; [key: string]: unknown }
  ): Promise<string> {
    const title = `💊 Medication Reminder - ${petName}`;
    const message = `Time to give ${petName} their ${medicationData.medicationName || 'medication'}.`;

    return this.createNotification({
      userId,
      petId,
      type: 'medication',
      priority: 'high',
      title,
      message,
      data: medicationData,
      actionUrl: '/health-insights?tab=timeline&filter=medications',
      actionLabel: 'Mark Given'
    });
  }

  /**
   * Create vaccination reminder notification
   */
  static async notifyVaccinationDue(
    userId: string,
    petId: string,
    petName: string,
    vaccinationData: { vaccineName?: string; [key: string]: unknown }
  ): Promise<string> {
    const title = `💉 Vaccination Due - ${petName}`;
    const message = `${petName} is due for ${vaccinationData.vaccineName || 'a vaccination'}. Schedule an appointment soon.`;

    return this.createNotification({
      userId,
      petId,
      type: 'vaccination',
      priority: 'medium',
      title,
      message,
      data: vaccinationData,
      actionUrl: '/calendar',
      actionLabel: 'Schedule'
    });
  }

  /**
   * Create review request notification
   */
  static async notifyReviewRequest(
    userId: string,
    petId: string,
    petName: string,
    documentData: { documentType?: string; documentId?: string; [key: string]: unknown }
  ): Promise<string> {
    const title = `📋 Review Required - ${petName}`;
    const message = `Please review the ${documentData.documentType || 'document'} for ${petName} to verify accuracy.`;

    return this.createNotification({
      userId,
      petId,
      type: 'review_request',
      priority: 'medium',
      title,
      message,
      data: documentData,
      actionUrl: `/documents/${documentData.documentId}`,
      actionLabel: 'Review Now'
    });
  }

  /**
   * Create expense notification (for vet bills from providers)
   */
  static async notifyExpenseReceived(
    userId: string,
    petId: string,
    petName: string,
    expenseData: { amount?: number; vendor?: string; category?: string; [key: string]: unknown }
  ): Promise<string> {
    const amount = expenseData.amount || 0;
    const vendor = expenseData.vendor || 'Provider';
    const category = expenseData.category || 'veterinary';
    
    const title = `💰 Vet Bill Received - ${petName}`;
    const message = `New ${category} expense of $${amount.toFixed(2)} from ${vendor} has been processed.`;

    return this.createNotification({
      userId,
      petId,
      type: 'expense',
      priority: 'medium',
      title,
      message,
      data: expenseData,
      actionUrl: '/pet-expenses',
      actionLabel: 'View Expense'
    });
  }

  /**
   * Create system notification
   */
  static async notifySystemMessage(
    userId: string,
    title: string,
    message: string,
    priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium',
    data?: Record<string, unknown>
  ): Promise<string> {
    return this.createNotification({
      userId,
      type: 'system',
      priority,
      title,
      message,
      data
    });
  }

  /**
   * Clean up expired notifications
   */
  static async cleanupExpiredNotifications(): Promise<void> {
    try {
      const cg = query(collectionGroup(db, 'notifications'), where('expiresAt', '<=', Timestamp.now()));
      const snap = await getDocs(cg);
      const updates = snap.docs.map(d => updateDoc(d.ref, { deleted: true }));
      await Promise.all(updates);
      NotificationService.log.info('Expired notifications cleaned up', { count: snap.docs.length });
    } catch (error) {
      NotificationService.log.error('Error cleaning up expired notifications', error as Error);
    }
  }

  // ========================================
  // ENHANCED HEALTH ALERT NOTIFICATIONS
  // Value-delivering notifications with insights from Claude AI
  // ========================================

  /**
   * Create health alert notification with value-delivering content
   * This is the primary method for lab result notifications
   * 
   * @param userId - User ID
   * @param petId - Pet ID
   * @param petName - Pet name
   * @param documentId - Source document ID
   * @param analysisResult - Claude AI analysis result containing health insights
   * @param labName - Lab provider name (e.g., "IDEXX Laboratories")
   * @param testDate - When the test was performed
   */
  static async createHealthAlertNotification(
    userId: string,
    petId: string,
    petName: string,
    documentId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- analysis result shape varies by processor version
    analysisResult: Record<string, any>,
    labName?: string,
    testDate?: Date
  ): Promise<string | null> {
    try {
      // Extract health concerns from Claude analysis
      const concerns = NotificationContentBuilder.extractHealthConcerns(analysisResult);
      
      if (concerns.length === 0) {
        NotificationService.log.debug('No health concerns found, skipping notification (no action needed)', { 
          userId, 
          petId, 
          documentId 
        });
        // Don't create notification for normal lab results
        return null;
      }

      // Determine severity
      const severity = NotificationContentBuilder.determineHealthSeverity(concerns);
      
      // Build health alert details
      const healthAlertDetails = NotificationContentBuilder.buildHealthAlertDetails(
        concerns,
        labName,
        testDate
      );

      // Build value-delivering title and body
      const title = NotificationContentBuilder.buildHealthAlertTitle(petName, severity, concerns);
      const body = NotificationContentBuilder.buildHealthAlertBody(concerns);
      const actionLabel = NotificationContentBuilder.getActionLabel(severity);

      // Determine priority
      const priority: NotificationPriority = 
        severity === 'critical' || severity === 'urgent' ? 'urgent' :
        severity === 'warning' ? 'high' : 'medium';

      // Create notification
      const notification = {
        userId,
        petId,
        category: 'health_alert' as NotificationCategory,
        priority,
        title,
        message: body,
        actionLabel,
        actionUrl: `/pets/${petId}/health/insights`,
        healthAlertDetails,
        sourceDocumentIds: [documentId],
        sourceEventType: 'lab_results_analyzed',
        channels: ['push', 'in_app']
      };

      NotificationService.log.info('Creating health alert notification', { 
        userId, 
        petId, 
        severity, 
        concernCount: concerns.length,
        metrics: concerns.map(c => c.metric)
      });

      return await this.createNotification(notification);
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'createHealthAlertNotification', userId, petId, documentId } });
      throw error;
    }
  }

  /**
   * Create "good news" notification when previously abnormal values return to normal
   * NOTE: This is disabled - we don't notify for processed lab results (not an action item)
   * 
   * @param userId - User ID
   * @param petId - Pet ID
   * @param petName - Pet name
   * @param documentId - Source document ID
   * @param improvingConcerns - Health concerns that have improved
   * @param labName - Lab provider name
   */
  static async createGoodNewsNotification(
    userId: string,
    petId: string,
    petName: string,
    documentId: string,
    improvingConcerns: HealthConcern[],
    labName?: string
  ): Promise<string | null> {
    // Don't create notifications for good news - not an action item
    NotificationService.log.debug('Skipping notification for good news lab results (no action needed)', { 
      userId, 
      petId, 
      documentId,
      improvingCount: improvingConcerns.length,
      labName 
    });
    return null;
  }

  /**
   * Create notification for normal lab results (no concerns)
   * Uses value-delivering content with provider name
   * 
   * @param userId - User ID
   * @param petId - Pet ID
   * @param petName - Pet name
   * @param documentId - Source document ID
   * @param labName - Lab provider name
   */
  static async createNormalLabResultNotification(
    userId: string,
    petId: string,
    petName: string,
    documentId: string,
    labName?: string
  ): Promise<string | null> {
    // Create persistent notification in notification bell
    // When clicked, it will navigate to the lab results and mark as read
    const providerText = labName ? ` from ${labName}` : '';
    return this.createNotification({
      userId,
      petId,
      type: 'lab_result',
      category: 'records_processed',
      priority: 'low',
      title: `✅ Lab Results Processed - ${petName}`,
      message: `Lab results for ${petName}${providerText} have been processed and added to your pet's health records.`,
      data: { 
        documentId,
        labName,
        isNormal: true,
        isInformational: true 
      },
      actionUrl: `/health-insights/lab-results/${petId}/${documentId}`,
      actionLabel: 'View Results'
    });
  }

  /**
   * Smart lab result notification that automatically determines the best notification type
   * This is the recommended method to use from document processing
   * 
   * @param userId - User ID
   * @param petId - Pet ID
   * @param petName - Pet name
   * @param documentId - Source document ID
   * @param analysisResult - Full Claude AI analysis
   * @param previousHadAbnormal - Whether previous test had abnormal values
   * @param labName - Lab provider name
   * @param testDate - Test date
   */
  static async createSmartLabResultNotification(
    userId: string,
    petId: string,
    petName: string,
    documentId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- analysis result shape varies by processor version
    analysisResult: Record<string, any>,
    previousHadAbnormal: boolean = false,
    labName?: string,
    testDate?: Date
  ): Promise<string | null> {
    try {
      // Extract concerns
      const concerns = NotificationContentBuilder.extractHealthConcerns(analysisResult);
      
      // Check for "good news" scenario
      if (NotificationContentBuilder.isGoodNews(concerns, previousHadAbnormal)) {
        // Get improving concerns from previous test
        const improvingConcerns = analysisResult.improvingConcerns || [];
        return await this.createGoodNewsNotification(
          userId, 
          petId, 
          petName, 
          documentId, 
          improvingConcerns, 
          labName
        );
      }
      
      // Check if has health concerns
      if (concerns.length > 0) {
        return await this.createHealthAlertNotification(
          userId,
          petId,
          petName,
          documentId,
          analysisResult,
          labName,
          testDate
        );
      }
      
      // All normal
      return await this.createNormalLabResultNotification(
        userId,
        petId,
        petName,
        documentId,
        labName
      );
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'createSmartLabResultNotification', userId, petId, documentId } });
      throw error;
    }
  }

  /**
   * Create refill request sent notification
   */
  static async notifyRefillRequestSent(
    userId: string,
    petId: string,
    petName: string,
    medicationName: string,
    vetName: string | null,
    emailSent: boolean
  ): Promise<string> {
    const title = emailSent
      ? `Refill Request Sent - ${petName}`
      : `Refill Request Saved - ${petName}`;
    const message = emailSent
      ? `Refill request for ${medicationName} has been sent to ${vetName}.`
      : `Refill request for ${medicationName} has been saved. Contact your vet to complete the refill.`;

    return this.createNotification({
      userId,
      petId,
      type: 'medication',
      category: 'care_task',
      priority: 'medium',
      title,
      message,
      data: { medicationName, vetName, emailSent },
      actionUrl: '/care-plan?tab=medications',
      actionLabel: 'View Details'
    });
  }

  /**
   * Create refill response notification (vet replied)
   */
  static async notifyRefillResponse(
    userId: string,
    petId: string,
    petName: string,
    medicationName: string,
    vetName: string,
    responseAction: 'approved' | 'denied' | 'needs_visit'
  ): Promise<string> {
    const actionText = responseAction === 'approved'
      ? 'approved your refill'
      : responseAction === 'needs_visit'
      ? 'says a visit is needed'
      : 'responded to your request';

    return this.createNotification({
      userId,
      petId,
      type: 'medication',
      category: 'action_required',
      priority: responseAction === 'approved' ? 'medium' : 'high',
      title: `Refill Update - ${petName}`,
      message: `${vetName} ${actionText} for ${medicationName}.`,
      data: { medicationName, vetName, responseAction },
      actionUrl: '/care-plan?tab=medications',
      actionLabel: responseAction === 'approved' ? 'Mark as Picked Up' : 'View Details'
    });
  }
}

export default NotificationService;
